<script setup>
import {computed, inject, toRef} from 'vue';
import {Field, useField} from 'vee-validate';
import {string} from 'yup';
import {defaultFieldProps} from '../helpers/defaultProps';
import {useDccsField} from '../composables/useDccsField';
import {useI18n} from 'vue-i18n';

const {t} = useI18n();

const props = defineProps({
    ...defaultFieldProps,

    type: {
        type: String,
        required: true,
        default: 'text',
    },
    customValidation: {
        type: String,
        required: false,
        default: null,
    },
    fieldMax: {
        type: String,
        required: false,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue']);
const busy = inject('busy');

const EMAILREG = new RegExp(/^([a-z0-9\.\-\_]+?)(@)([a-z0-9\.\-\_]+)$/);
const TELREG = new RegExp(/(\+\d{1,2}|00\d{1,2}|0\d{0,1})(\+\d{1,2}|00\d{1,2}|0|\s?)?((\(\d{1,3}\)\s?)|(\d{1,3})(\s|-?))(\d{1,3}(\s|-?))(\d{3}(\s|-?))(\d{1,30})(\s?(([E|e]xt[:|.|]?)|x|X)(\s?\d+))?/);
const CONTAINS_STRING = new RegExp(/([a-zA-Z]&?)/);
const DATEREG = new RegExp(/^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/);

const {normalizedRequired, labelText} = useDccsField(props);
const validationRules = computed(() => {
    let validator = string();

    if (props.customValidation && props.customValidation.length > 0 && props.customValidation !== 'none') {
        if (props.customValidation === 'telephone-email') {
            validator = validator.test('telephone-email', '', value => EMAILREG.test(value) || (TELREG.test(value) && !CONTAINS_STRING.test(value)));
        } else if (props.customValidation === 'email') {
            validator = validator.test('email', '', value => EMAILREG.test(value));
        } else if (props.customValidation === 'telephone') {
            validator = validator.test('telephone', '', value => TELREG.test(value) && !CONTAINS_STRING.test(value));
        } else if (props.customValidation === 'date') {
            validator = validator.test('date', '', value => DATEREG.test(value));
        }

    } else {
        if (props.type === 'email') {
            validator = validator.email(t('form.validation.email'));
        }

        if (props.type === 'url') {
            validator = validator.url(t('form.validation.url'));
        }
    }

    if (normalizedRequired.value === true) {
        validator = validator.required('');
    }

    return validator;
});

const refName = toRef(props, 'name');
const {errorMessage, value, resetField} = useField(refName, validationRules);

resetField({
    value: '',
});

const uniqueId = (name) =>{
    let uniqueName = name;
    if(uniqueName) {
        uniqueName = uniqueName.toLowerCase().replace(/[^\x00-\x7F]/g, "").replace(/\s/g, "-");
        return uniqueName;
    }

    return "";
}
</script>

<template>
    <Field v-slot="{ field, meta, errors }" v-model="value" as="div" :name="name" :label="label" :rules="validationRules" :validateOnInput="true" :validateOnChange="true" class="input-field">
        <input
            v-bind="field"
            :id="uniqueId(name)"
            :name="name"
            :ref="refName"
            :class="['merkur-input ', { 'has-error': meta.touched || errors.length }, { 'is-danger': meta.validated && !meta.valid  || errors.length }]"
            :placeholder="placeholder"
            :disabled="busy"
            :type="type"
            :data-validation="customValidation"
            :required="normalizedRequired"
            :maxlength="fieldMax > 0 ? fieldMax : ''"
        />
        <label v-if="label && label.length" class="merkur-label active" :for="uniqueId(name)">
            {{ labelText }}<template v-if="normalizedRequired">*</template>
        </label>
    </Field>
</template>
